import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import { PainPoint } from '@/types/journey';

interface PainPointFieldProps {
  label: string;
  painPoints: PainPoint[];
  onChange: (painPoints: PainPoint[]) => void;
  placeholder?: string;
}

const PainPointField: React.FC<PainPointFieldProps> = ({
  label,
  painPoints,
  onChange,
  placeholder = "Add a pain point..."
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempPainPoints, setTempPainPoints] = useState<PainPoint[]>(painPoints || []);
  const [newPainPoint, setNewPainPoint] = useState('');

  useEffect(() => {
    setTempPainPoints(painPoints || []);
  }, [painPoints]);

  const handleAddPainPoint = () => {
    if (!newPainPoint.trim()) return;
    
    const newPainPointItem: PainPoint = {
      id: `pain-point-${Date.now()}`,
      text: newPainPoint.trim()
    };
    
    setTempPainPoints([...tempPainPoints, newPainPointItem]);
    setNewPainPoint('');
  };

  const handleRemovePainPoint = (id: string) => {
    setTempPainPoints(tempPainPoints.filter(painPoint => painPoint.id !== id));
  };

  const handleSave = () => {
    onChange(tempPainPoints);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempPainPoints(painPoints || []);
    setIsEditing(false);
    setNewPainPoint('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPainPoint();
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <label className="text-[11px] font-medium block text-muted-foreground">
          {label}
        </label>
        
        <div className="space-y-2">
          {/* Pain Point list */}
          <div className="flex flex-wrap gap-1">
            {tempPainPoints.map((painPoint) => (
              <div 
                key={painPoint.id}
                className="group bg-red-50 dark:bg-red-900/20 text-xs px-2 py-1 rounded-full flex items-center gap-1 border border-red-200 dark:border-red-800/30"
              >
                <Wrench className="h-3 w-3 text-red-500 dark:text-red-400 mr-0.5" />
                {painPoint.text}
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-muted-foreground hover:text-foreground"
                  onClick={() => handleRemovePainPoint(painPoint.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          
          {/* Add new pain point */}
          <div className="flex gap-1">
            <div className="flex-1 relative">
              <Input
                type="text"
                value={newPainPoint}
                onChange={(e) => setNewPainPoint(e.target.value)}
                placeholder={placeholder}
                className="text-xs"
                onKeyDown={handleKeyDown}
              />
              {newPainPoint && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-5 w-5 p-0"
                  onClick={() => setNewPainPoint('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={handleAddPainPoint}
              disabled={!newPainPoint.trim()}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          
          <div className="flex justify-end space-x-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-6 text-xs"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium block text-muted-foreground">
        {label}
      </label>
      
      {tempPainPoints.length > 0 ? (
        <div 
          className="flex flex-wrap gap-1 min-h-[2.5rem] cursor-pointer hover:bg-muted/40 p-1 rounded"
          onClick={() => setIsEditing(true)}
        >
          {tempPainPoints.map((painPoint) => (
            <div 
              key={painPoint.id}
              className="bg-red-50 dark:bg-red-900/20 text-xs px-2 py-1 rounded-full flex items-center gap-1 border border-red-200 dark:border-red-800/30"
            >
              <Wrench className="h-3 w-3 text-red-500 dark:text-red-400 mr-0.5" />
              {painPoint.text}
            </div>
          ))}
        </div>
      ) : (
        <div 
          className="text-xs min-h-[2.5rem] cursor-pointer hover:bg-muted/40 p-1 rounded text-muted-foreground italic"
          onClick={() => setIsEditing(true)}
        >
          {placeholder}
        </div>
      )}
    </div>
  );
};

export default PainPointField; 